import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo, LogoMark } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Home, ClipboardList, NotebookPen, MessageCircle, BookOpen, CalendarDays,
  LifeBuoy, LayoutDashboard, Users, Settings, LogOut, Menu, X, HeartHandshake, Bell, Crown, RotateCcw,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { usePremium } from "@/components/Paywall";
import BottomNav from "@/components/BottomNav";
import { enablePush } from "@/lib/push";

const NAV = {
  patient: [
    { to: "/", label: "Início", icon: Home },
    { to: "/atividades", label: "Atividades", icon: ClipboardList },
    { to: "/registro", label: "Meu Registro", icon: NotebookPen },
    { to: "/mensagens", label: "Mensagens", icon: MessageCircle },
    { to: "/agenda", label: "Agenda", icon: CalendarDays },
    { to: "/rede-apoio", label: "Minha rede de apoio", icon: HeartHandshake },
    { to: "/conteudos", label: "Conteúdos", icon: BookOpen },
  ],
  professional: [
    { to: "/", label: "Painel", icon: LayoutDashboard },
    { to: "/pacientes", label: "Pacientes", icon: Users },
    { to: "/agenda", label: "Agenda", icon: CalendarDays },
  ],
  org_admin: [
    { to: "/", label: "Painel", icon: LayoutDashboard },
    { to: "/pacientes", label: "Pacientes", icon: Users },
    { to: "/admin", label: "Organização", icon: Settings },
  ],
};

export default function AppShell({ children }) {
  const { user, setUser, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const items = NAV[user?.role] || [];
  const isPatient = user?.role === "patient";

  const roleLabel = { patient: "Paciente", professional: "Profissional", org_admin: "Admin da Organização" }[user?.role];

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* top bar */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-[#E0F2FE]">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-[#1F3B4D]" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
            <button onClick={() => nav("/")} data-testid="logo-home-button">
              <Logo />
            </button>
          </div>
          <div className="flex items-center gap-2">
            {isPatient && (
              <Button
                onClick={() => nav("/apoio")}
                data-testid="pedir-ajuda-topbar-button"
                className="rounded-full bg-[#EF4444] hover:bg-[#dc2626] text-white gap-2 h-9 shadow-md shadow-red-500/20"
              >
                <LifeBuoy size={16} /> <span className="hidden sm:inline">Pedir ajuda</span>
              </Button>
            )}
            <div className="hidden sm:flex flex-col items-end mr-1">
              <span className="text-sm font-semibold text-[#1F3B4D] leading-none">{user?.name}</span>
              <span className="text-[11px] text-[#94A3B8]">{roleLabel}</span>
            </div>
            <div className="w-9 h-9 rounded-full bg-[#0F766E] text-white grid place-items-center font-semibold text-sm">
              {user?.name?.[0]?.toUpperCase()}
            </div>
            <Button variant="ghost" size="icon" onClick={() => setSettingsOpen(true)} data-testid="open-settings-button" title="Meu perfil">
              <Settings size={18} className="text-[#94A3B8]" />
            </Button>
            <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-button" title="Sair">
              <LogOut size={18} className="text-[#94A3B8]" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto flex">
        {/* sidebar */}
        <aside className={`${open ? "block" : "hidden"} lg:block fixed lg:sticky top-16 z-30 lg:z-0 h-[calc(100vh-4rem)] w-64 shrink-0 bg-white lg:bg-transparent border-r border-[#E0F2FE] lg:border-0 px-3 py-5`}>
          <nav className="space-y-1">
            {items.map((it) => {
              const active = it.to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(it.to);
              const Icon = it.icon;
              return (
                <button
                  key={it.to}
                  data-testid={`nav-${it.label.toLowerCase().replace(/\s/g, "-")}`}
                  onClick={() => { nav(it.to); setOpen(false); }}
                  className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                    active ? "bg-[#0F766E] text-white shadow-sm" : "text-[#1F3B4D] hover:bg-[#E0F2FE]"
                  }`}
                >
                  <Icon size={18} /> {it.label}
                </button>
              );
            })}
          </nav>
          <div className="mt-8 mx-2 p-4 rounded-2xl bg-gradient-to-br from-[#E0F2FE] to-[#34D399]/15 border border-[#E0F2FE]">
            <div className="flex items-center gap-2 mb-1">
              <LogoMark size={22} />
              <span className="text-xs font-semibold text-[#0F766E]">Compasso</span>
            </div>
            <p className="text-[11px] text-[#1F3B4D]/70 leading-snug">
              Cuidado que continua entre as sessões.
            </p>
          </div>
        </aside>
        {open && <div className="lg:hidden fixed inset-0 top-16 z-20 bg-black/20" onClick={() => setOpen(false)} />}

        <main className="flex-1 min-w-0 px-4 sm:px-6 pt-6 pb-28 lg:py-8">{children}</main>
      </div>

      <BottomNav />
      <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} user={user} onSaved={setUser} />
    </div>
  );
}

function SettingsDialog({ open, onOpenChange, user, onSaved }) {
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setName(user?.name || ""); setEmail(user?.email || ""); }, [user, open]);
  const isPro = user?.role === "professional" || user?.role === "org_admin";
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/auth/profile", { name, email });
      onSaved(data);
      toast.success("Perfil atualizado.");
      onOpenChange(false);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const enablePushHandler = async () => {
    try { await enablePush(); toast.success("Notificações ativadas neste dispositivo."); }
    catch (e) { toast.error(e.message || "Não foi possível ativar as notificações."); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl">
        <DialogHeader><DialogTitle className="font-heading text-[#1F3B4D]">Meu perfil</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1"><Label>Nome</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="settings-name-input" className="rounded-xl border-[#E0F2FE]" /></div>
          <div className="space-y-1"><Label>E-mail{isPro ? " do conselheiro" : ""}</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="settings-email-input" className="rounded-xl border-[#E0F2FE]" placeholder="voce@email.com" />
            {isPro && <p className="text-xs text-[#94A3B8]">Usaremos este e-mail para enviar alertas de pedidos de urgência e fissura dos seus pacientes.</p>}
          </div>
          {isPro && (
            <Button variant="outline" onClick={enablePushHandler} data-testid="enable-push-button" className="w-full rounded-xl border-[#E0F2FE] gap-2">
              <Bell size={15} /> Ativar notificações push neste dispositivo
            </Button>
          )}
          <SubscriptionSection />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="save-settings-button" className="rounded-full bg-[#0F766E] hover:bg-[#0d6259]">Salvar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SubscriptionSection() {
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState(false);
  const premium = usePremium();
  useEffect(() => { api.get("/me/subscription").then((r) => setSub(r.data)).catch(() => {}); }, []);
  const isPremium = sub?.status === "premium";
  const restore = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/me/subscription/restore");
      setSub(data);
      toast.success(data.status === "premium" ? "Premium restaurado." : "Nenhuma compra ativa encontrada.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-[#E0F2FE] p-3 space-y-2" data-testid="subscription-section">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold text-[#1F3B4D]"><Crown size={16} className="text-[#F59E0B]" /> Assinatura</div>
        <Badge className={isPremium ? "bg-[#0F766E] text-white border-0" : "bg-[#E0F2FE] text-[#0F766E] border-0"} data-testid="subscription-plan-badge">{isPremium ? "PREMIUM" : "FREE"}</Badge>
      </div>
      <div className="text-xs text-[#94A3B8]">Plano atual: <span className="font-semibold text-[#1F3B4D]">{isPremium ? "Premium ativo" : "Free"}</span></div>
      <div className="flex gap-2 flex-wrap">
        {!isPremium && (
          <Button size="sm" data-testid="know-premium-button" onClick={() => premium?.openPaywall()} className="rounded-full bg-[#0F766E] hover:bg-[#0d6259] gap-1"><Crown size={14} /> Conhecer Premium</Button>
        )}
        <Button size="sm" variant="outline" data-testid="restore-purchases-button" onClick={restore} disabled={busy} className="rounded-full border-[#E0F2FE] gap-1"><RotateCcw size={14} /> Restaurar compras</Button>
      </div>
    </div>
  );
}
